// src/pages/Dashboard/Clientes/components/AssociateModal.tsx
import React, { useState, useEffect } from 'react';
import styles from './ClienteEditModal.module.css';
import api from '@/utils/connector/ApiConnector';
import { useUser } from '@/utils/userContext/UserContext';
import { useGlobalModal } from '@/components/GlobalModalProvider/GlobalModalProvider';
import { X } from 'lucide-react';
import { phoneNumberHelper } from '@/utils/common/phoneNumberHelper';

type Associate = {
  id?: string;
  name: string;
  email: string;
  phone?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (associate: Associate) => void;
  associate: Associate | null;
  customerId: string;
};

const AssociateModal: React.FC<Props> = ({ open, onClose, onSave, associate, customerId }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const modal = useGlobalModal();

  const isEditing = !!associate;

  useEffect(() => {
    if (associate) {
      setName(associate.name || '');
      setEmail(associate.email || '');
      setPhone(associate.phone ? phoneNumberHelper.formatPhone(associate.phone) : '');
    } else {
      setName('');
      setEmail('');
      setPhone('');
    }
  }, [associate]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload: Associate = {
      name,
      email,
      phone: phone ? phoneNumberHelper.cleanPhone(phone) : undefined,
    };

    // For new customers, customerId will be empty. We just save to parent state.
    if (!customerId) {
      onSave(payload);
      onClose();
      return;
    }

    if (!token) {
      modal.show({ type: 'error', title: 'Erro', message: 'Sessão expirada. Faça login novamente.' });
      return;
    }

    if (!name || !email) {
      modal.show({ type: 'error', title: 'Erro', message: 'Nome e Email são obrigatórios.' });
      return;
    }

    setSaving(true);
    try {
      if (isEditing && associate?.id) {
        await api.patch(`/customer/${customerId}/associates/${associate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        modal.show({ type: 'success', title: 'Sucesso', message: 'Associado atualizado com sucesso.', autoClose: 3000 });
      } else {
        await api.post(`/customer/${customerId}/associates`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        modal.show({ type: 'success', title: 'Sucesso', message: 'Associado criado com sucesso.', autoClose: 3000 });
      }
      onSave(payload);
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar associado:', err);
      const message = err?.response?.data?.message ?? err.message ?? 'Erro ao salvar associado.';
      modal.show({ type: 'error', title: 'Erro', message, autoClose: 4500 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.flexSeparator}>
          <h2>{isEditing ? 'Editar Associado' : 'Novo Associado'}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Fechar modal">
            <X size={18} />
          </button>
        </div>

        <div>
          <p className={styles.modalSubtitle}>
            Preencha os dados para {isEditing? 'editar':'cadastrar'} o associado.
          </p>
          <p className={styles.requiredLegend}>
            <span className={styles.requiredMark}>*</span> Campos obrigatorios
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.formFieldsContainer} style={{ gridTemplateColumns: '1fr' }}>
            <label>
              Nome <span className={styles.requiredMark}>*</span>
              <input
                className={styles.inputField}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
            <label>
              Email <span className={styles.requiredMark}>*</span>
              <input
                type="email"
                className={styles.inputField}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              Telefone
              <input
                className={styles.inputField}
                value={phone}
                onChange={(e) => setPhone(phoneNumberHelper.formatPhone(e.target.value))}
              />
            </label>
          </div>
          <div className={styles.clienteExpandAreaButtons} style={{ marginTop: 20 }}>
            <button type="submit" className={styles.primaryButton} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={onClose}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssociateModal;
